'use client'

import { Suspense, useEffect, useRef, useState, useCallback } from 'react'
import { useSearchParams } from 'next/navigation'
import { useSession } from 'next-auth/react'
import Link from 'next/link'
import { FglHeader } from '@/components/layout/fgl-header'
import { GliderBackdrop } from '@/components/award'
import { MapDisclaimer } from '@/components/legal/map-disclaimer'
import { useTranslation } from '@/i18n/client'
import Script from 'next/script'

// Cesium Types (loaded via Script tag)
declare const Cesium: any

export default function Airspace3DPage() {
  return (
    <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-[var(--muted)]">...</div></div>}>
      <Airspace3DContent />
    </Suspense>
  )
}

function Airspace3DContent() {
  const { t } = useTranslation()
  const { data: session, status: sessionStatus } = useSession()
  const [userPlan, setUserPlan] = useState<string | null>(null)
  const [checkingAccess, setCheckingAccess] = useState(true)

  // Feature-Gate: Nur Pro + Premium
  useEffect(() => {
    if (sessionStatus === 'loading') return
    if (sessionStatus === 'unauthenticated' || !session) {
      setUserPlan(null)
      setCheckingAccess(false)
      return
    }
    let cancelled = false
    async function checkAccess() {
      try {
        const res = await fetch('/api/subscription')
        if (cancelled) return
        if (res.ok) {
          const data = await res.json()
          const flights = data?.data?.progress?.validFlights ?? data?.validFlights ?? 0
          const planCode = data?.data?.plan?.code
          if (planCode === 'premium' || flights >= 100) setUserPlan('premium')
          else if (planCode === 'pro' || flights >= 25) setUserPlan('pro')
          else setUserPlan('start')
        } else {
          setUserPlan('start')
        }
      } catch {
        if (!cancelled) setUserPlan('start')
      }
      if (!cancelled) setCheckingAccess(false)
    }
    checkAccess()
    return () => { cancelled = true }
  }, [session, sessionStatus])

  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false)
  const handleDisclaimerAccept = useCallback(() => setDisclaimerAccepted(true), [])
  const containerRef = useRef<HTMLDivElement>(null)
  const viewerRef = useRef<any>(null)
  const [loaded, setLoaded] = useState(false)
  const [status, setStatus] = useState('loadingCesium')
  const [airspaceCount, setAirspaceCount] = useState(0)
  const [notamCount, setNotamCount] = useState(0)
  const [fieldsCount, setFieldsCount] = useState(0)
  const [showAirspace, setShowAirspace] = useState(true)
  const [showNotams, setShowNotams] = useState(false)
  const [showFields, setShowFields] = useState(true)
  const [golfMode, setGolfMode] = useState(false)
  const [airspaceLoaded, setAirspaceLoaded] = useState(false)
  const golfModeRef = useRef(false)
  const showFieldsRef = useRef(true)
  const showAirspaceRef = useRef(true)
  const showNotamsRef = useRef(true)
  const allFieldsRef = useRef<any[]>([])
  const searchParams = useSearchParams()
  const urlLat = searchParams.get('lat')
  const urlLon = searchParams.get('lon')
  const urlName = searchParams.get('name')

  const accessDenied = !checkingAccess && (!session || userPlan === 'start')

  // Cesium initialisieren nach Script-Load
  const initCesium = () => {
    if (!containerRef.current || viewerRef.current) return

    // Cesium Ion Free Tier Token (kostenlos registrieren auf cesium.com)
    // Ohne Token: Kein 3D-Terrain, nur flache Karte
    // Mit Token: Echtes 3D-Terrain weltweit (Cesium World Terrain)
    const ionToken = (window as any).__CESIUM_ION_TOKEN
    if (ionToken) {
      Cesium.Ion.defaultAccessToken = ionToken
    }

    const viewer = new Cesium.Viewer(containerRef.current, {
      baseLayerPicker: false,
      geocoder: false,
      homeButton: true,
      sceneModePicker: true,
      navigationHelpButton: false,
      animation: false,
      timeline: false,
      fullscreenButton: true,
      vrButton: false,
      selectionIndicator: true,
      infoBox: true,
      imageryProvider: false as any,
      terrain: ionToken ? Cesium.Terrain.fromWorldTerrain() : undefined,
    })

    // OSM Tiles als Basiskarte
    viewer.imageryLayers.addImageryProvider(
      new Cesium.OpenStreetMapImageryProvider({ url: 'https://tile.openstreetmap.org/' })
    )

    // Atmosphere + Rendering
    viewer.scene.skyAtmosphere.show = true
    viewer.scene.globe.enableLighting = false
    viewer.scene.globe.depthTestAgainstTerrain = false

    // Kamera bleibt über dem Terrain (nicht unter den Boden)
    viewer.scene.screenSpaceCameraController.enableCollisionDetection = true
    viewer.scene.screenSpaceCameraController.minimumZoomDistance = 5 // Min 5m über Grund

    // InfoBox sandbox komplett entfernen damit onclick + postMessage funktioniert
    const infoBox = viewer.infoBox
    if (infoBox?.frame) {
      infoBox.frame.removeAttribute('sandbox')
      // Nach jedem InfoBox-Update sandbox erneut entfernen (Cesium setzt sie zurück)
      const observer = new MutationObserver(() => {
        if (infoBox.frame.hasAttribute('sandbox')) {
          infoBox.frame.removeAttribute('sandbox')
        }
      })
      observer.observe(infoBox.frame, { attributes: true, attributeFilter: ['sandbox'] })
    }

    // postMessage Listener für Button-Klicks aus dem InfoBox-iframe
    window.addEventListener('message', (event: any) => {
      if (event.data?.type === 'flyToField') {
        ;(window as any).__flyToField(event.data.lat, event.data.lon)
      }
      if (event.data?.type === 'navigate') {
        window.location.href = event.data.url
      }
    })

    // Doppelklick auf Entity = direkt zum Platz fliegen
    viewer.screenSpaceEventHandler.setInputAction((click: any) => {
      const picked = viewer.scene.pick(click.position)
      if (picked?.id?.properties?.type?.getValue() === 'field') {
        const pos = picked.id.position?.getValue(viewer.clock.currentTime)
        if (pos) {
          const carto = Cesium.Cartographic.fromCartesian(pos)
          const lat = Cesium.Math.toDegrees(carto.latitude)
          const lon = Cesium.Math.toDegrees(carto.longitude)
          ;(window as any).__flyToField(lat, lon)
        }
      }
    }, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK)

    // Klick-Handler: Flugplatz-InfoBox ODER Golf-Berechnung
    viewer.screenSpaceEventHandler.setInputAction((click: any) => {
      const picked = viewer.scene.pick(click.position)

      // Klick auf Flugplatz → InfoBox öffnen (egal ob Golf-Modus oder nicht)
      if (picked?.id?.properties?.type?.getValue() === 'field') {
        viewer.selectedEntity = picked.id
        return
      }

      // Klick auf Airspace-Entity → InfoBox öffnen wenn NICHT im Golf-Modus
      if (picked?.id?.properties?.type?.getValue() === 'airspace' && !golfModeRef.current) {
        viewer.selectedEntity = picked.id
        return
      }

      // Golf-Modus: Klick auf Boden oder Airspace → Golf-Höhe berechnen
      if (!golfModeRef.current) return

      const ray = viewer.camera.getPickRay(click.position)
      if (!ray) return
      const cartesian = viewer.scene.globe.pick(ray, viewer.scene)
      if (!cartesian) return
      const carto = Cesium.Cartographic.fromCartesian(cartesian)
      const clickLat = Cesium.Math.toDegrees(carto.latitude)
      const clickLon = Cesium.Math.toDegrees(carto.longitude)
      const groundM = carto.height || 0

      // Finde den niedrigsten Nicht-Golf-Luftraum über diesem Punkt
      // Das ist die Untergrenze (lowerLimit) die Golf nach oben beschränkt
      const airspacesAbove: { name: string; cls: string; lowerM: number; upperM: number; lowerLabel: string }[] = []

      viewer.entities.values.forEach((e: any) => {
        if (e.properties?.type?.getValue() !== 'airspace') return
        if (!e.polygon) return
        const icaoClass = e.properties?.icaoClass?.getValue()
        if (icaoClass === 6) return // G selbst überspringen

        // Verwaltungs-Lufträume ignorieren — beschränken Golf NICHT
        // Type: 10=FIR, 11=UIR, 12=ADIZ, 15=AIRWAY, 27=ACC_SECTOR, 0=OTHER(oft FIR)
        const asType = e.properties?.airspaceType?.getValue()
        if ([10, 11, 12, 15, 27].includes(asType)) return

        // SUA (Klasse 8) mit Typ 0 (OTHER) → prüfe ob es ein FIR/FIS/UIR ist
        // Diese sind Verwaltungs-Lufträume und beschränken Golf NICHT
        if (icaoClass === 8) {
          const nameStr = (e.name || '').toUpperCase()
          if (nameStr.includes('FIR') || nameStr.includes('UIR') || nameStr.includes('FIS') ||
              nameStr.includes('EDGG') || nameStr.includes('EDMM') || nameStr.includes('EDWW') ||
              nameStr.includes('EDUU') || nameStr.includes('ACC') ||
              nameStr.includes('SHANNON') || nameStr.includes('LONDON') || nameStr.includes('PARIS') ||
              nameStr.includes('REIMS') || nameStr.includes('BREST') || nameStr.includes('BORDEAUX') ||
              nameStr.includes('MARSEILLE') || nameStr.includes('WIEN') || nameStr.includes('ZURICH') ||
              nameStr.includes('MILANO') || nameStr.includes('ROMA') || nameStr.includes('MADRID') ||
              nameStr.includes('MAASTRICHT') || nameStr.includes('PRAHA') || nameStr.includes('WARSZAWA') ||
              nameStr.includes('SECTOR') || nameStr.includes('INFORMATION')) return
        }

        const hierarchy = e.polygon.hierarchy?.getValue()
        if (!hierarchy?.positions) return

        // Point-in-Polygon
        let inside = false
        const pos = hierarchy.positions
        for (let i = 0, j = pos.length - 1; i < pos.length; j = i++) {
          const ci = Cesium.Cartographic.fromCartesian(pos[i])
          const cj = Cesium.Cartographic.fromCartesian(pos[j])
          const yi = Cesium.Math.toDegrees(ci.latitude)
          const xi = Cesium.Math.toDegrees(ci.longitude)
          const yj = Cesium.Math.toDegrees(cj.latitude)
          const xj = Cesium.Math.toDegrees(cj.longitude)
          if (((yi > clickLat) !== (yj > clickLat)) && (clickLon < (xj - xi) * (clickLat - yi) / (yj - yi) + xi))
            inside = !inside
        }
        if (!inside) return

        // Unterkante dieses Luftraums = Golf-Decke
        // Bei GND-referenzierten Höhen: Bodenhöhe addieren für MSL-Vergleich
        const rawLowerM = e.polygon.height?.getValue() || 0
        const rawUpperM = e.polygon.extrudedHeight?.getValue() || 0
        const isLowerGNDProp = e.properties?.lowerGND?.getValue() || false
        const isUpperGNDProp = e.properties?.upperGND?.getValue() || false
        // GND-Werte + Bodenhöhe = MSL; MSL-Werte bleiben wie sie sind
        const lowerMSL = isLowerGNDProp ? rawLowerM + groundM : rawLowerM
        const upperMSL = isUpperGNDProp ? rawUpperM + groundM : rawUpperM
        const names = ['A','B','C','D','E','F','G','?','SUA']
        const cls = names[icaoClass] || '?'

        airspacesAbove.push({
          name: e.name || '?',
          cls,
          lowerM: Math.min(lowerMSL, upperMSL),
          upperM: Math.max(lowerMSL, upperMSL),
          lowerLabel: `${Math.round(Math.min(lowerMSL, upperMSL))}m MSL`,
        })
      })

      // Sortiere nach Unterkante — die niedrigste ist die Golf-Decke
      airspacesAbove.sort((a, b) => a.lowerM - b.lowerM)
      const golfCeiling = airspacesAbove.length > 0 ? airspacesAbove[0] : null
      const golfM = golfCeiling ? Math.max(0, Math.round(golfCeiling.lowerM - groundM)) : null
      const golfFt = golfM !== null ? Math.round(golfM / 0.3048) : null

      // Alte Golf-Labels entfernen
      const old = viewer.entities.getById('golf-info')
      if (old) viewer.entities.remove(old)

      // Schichtung aufbauen
      let schichtung = ''
      if (airspacesAbove.length > 0) {
        schichtung = '\n--- Schichtung ---'
        airspacesAbove.slice(0, 5).forEach(a => {
          schichtung += `\n${a.cls}: ${Math.round(a.lowerM)}m — ${Math.round(a.upperM)}m ${a.name.slice(0, 25)}`
        })
      }

      // Golf-Infoschild — groß, deutlich, AGL + MSL klar getrennt
      const isOk = golfM !== null && golfM > 0
      const golfFtAGL = golfFt // bereits AGL
      const ceilingMSL = golfCeiling ? Math.round(golfCeiling.lowerM) : 0
      const ceilingFtMSL = golfCeiling ? Math.round(golfCeiling.lowerM / 0.3048) : 0

      const golfText = golfM !== null
        ? [
            `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
            `  GOLF (G):  ${golfM}m / ${golfFtAGL} ft AGL  frei`,
            `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
            ``,
            `  Boden:      ${Math.round(groundM)}m MSL  (${Math.round(groundM / 0.3048)} ft MSL)`,
            `  G-Decke:    ${ceilingMSL}m MSL  (${ceilingFtMSL} ft MSL)`,
            `  Frei AGL:   ${golfM}m  /  ${golfFtAGL} ft  über Grund`,
            ``,
            `  Begrenzt durch: ${golfCeiling!.cls} — ${golfCeiling!.name.slice(0, 22)}`,
            ``,
            `  ─── Luftraum-Schichtung ───`,
            ...airspacesAbove.slice(0, 5).map(a => {
              const aglLow = Math.round(a.lowerM - groundM)
              return `  ${a.cls}:  ${Math.round(a.lowerM)}m MSL (${aglLow}m AGL) — ${Math.round(a.upperM)}m MSL`
            }),
            `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
            `  Ohne Gewähr — siehe Haftungsausschluss`,
          ].join('\n')
        : [
            `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
            `  GOLF (G):  KEIN LUFTRAUM ERKANNT`,
            `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
            `  Boden: ${Math.round(groundM)}m MSL`,
            ``,
            `  Lufträume ggf. nicht geladen`,
            `  → Näher heranzoomen + LADEN klicken`,
            `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
            `  Ohne Gewähr — siehe Haftungsausschluss`,
          ].join('\n')

      viewer.entities.add({
        id: 'golf-info',
        position: cartesian,
        label: {
          text: golfText,
          font: 'bold 14px monospace',
          fillColor: Cesium.Color.WHITE,
          showBackground: true,
          backgroundColor: isOk
            ? Cesium.Color.fromCssColorString('#16a34a').withAlpha(0.95)
            : Cesium.Color.fromCssColorString('#dc2626').withAlpha(0.95),
          backgroundPadding: new Cesium.Cartesian2(14, 10),
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 3,
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          pixelOffset: new Cesium.Cartesian2(0, -25),
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
        },
        point: {
          pixelSize: 16,
          color: isOk ? Cesium.Color.fromCssColorString('#22c55e') : Cesium.Color.RED,
          outlineColor: Cesium.Color.WHITE,
          outlineWidth: 4,
          heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
      })
      setTimeout(() => { const i = viewer.entities.getById('golf-info'); if (i) viewer.entities.remove(i) }, 30000)
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK)

    // Globale Funktion: Zum Flugplatz fliegen — schräg von der Seite (Südwest)
    // So sieht man den Platz UND den Luftraum darüber
    ;(window as any).__flyToField = (lat: number, lon: number) => {
      // Erst Lufträume im Umkreis laden
      loadAirspace(viewer, lat, lon)

      // Offset: Kamera ~500m südwestlich, 200m über Grund, Blick Richtung NE nach oben
      const offsetLat = -0.003 // ~330m südlich
      const offsetLon = -0.004 // ~300m westlich

      const cartographic = Cesium.Cartographic.fromDegrees(lon, lat)
      const terrainProvider = viewer.terrainProvider
      if (terrainProvider?.ready !== false) {
        Cesium.sampleTerrainMostDetailed(terrainProvider, [cartographic]).then((updated: any) => {
          const groundHeight = updated[0]?.height || 0
          viewer.camera.flyTo({
            destination: Cesium.Cartesian3.fromDegrees(lon + offsetLon, lat + offsetLat, groundHeight + 250),
            orientation: {
              heading: Cesium.Math.toRadians(35),  // Blick nach Nordost
              pitch: Cesium.Math.toRadians(-25),    // Leicht nach unten auf den Platz
              roll: 0,
            },
            duration: 2.5,
          })
        }).catch(() => {
          viewer.camera.flyTo({
            destination: Cesium.Cartesian3.fromDegrees(lon + offsetLon, lat + offsetLat, 400),
            orientation: { heading: Cesium.Math.toRadians(35), pitch: Cesium.Math.toRadians(-25), roll: 0 },
            duration: 2.5,
          })
        })
      } else {
        viewer.camera.flyTo({
          destination: Cesium.Cartesian3.fromDegrees(lon + offsetLon, lat + offsetLat, 400),
          orientation: { heading: Cesium.Math.toRadians(35), pitch: Cesium.Math.toRadians(-25), roll: 0 },
          duration: 2.5,
        })
      }
    }

    // Home-Button → Europa statt globale Ansicht
    viewer.homeButton.viewModel.command.beforeExecute.addEventListener((e: any) => {
      e.cancel = true
      viewer.camera.flyTo({
        destination: Cesium.Cartesian3.fromDegrees(10.5, 48.5, 2500000),
        orientation: {
          heading: Cesium.Math.toRadians(0),
          pitch: Cesium.Math.toRadians(-40),
          roll: 0,
        },
        duration: 2,
      })
    })

    // Untergrund leicht transparent machen damit man Golf erkennen kann
    viewer.scene.globe.translucency.enabled = true
    viewer.scene.globe.translucency.frontFaceAlpha = 1.0
    viewer.scene.globe.translucency.backFaceAlpha = 0.4

    // Auf Zielposition oder Deutschland zentrieren
    const startLat = urlLat ? parseFloat(urlLat) : 51.0
    const startLon = urlLon ? parseFloat(urlLon) : 10.5
    const startAlt = urlLat ? 5000 : 800000 // Nah bei URL-Parameter, weit bei Default

    viewer.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(startLon, startLat, startAlt),
      orientation: {
        heading: Cesium.Math.toRadians(0),
        pitch: Cesium.Math.toRadians(urlLat ? -30 : -45),
        roll: 0,
      },
      duration: 2,
    })

    viewerRef.current = viewer
    setLoaded(true)
    setStatus('ready')

    // Daten laden
    loadAirspace(viewer, startLat, startLon)
    // loadNotams deaktiviert — nur FAA/USA, für Europa nicht nutzbar

    // Flugplätze: warten bis GeoJSON geladen ist
    const waitForFields = setInterval(() => {
      if (allFieldsRef.current.length > 0) {
        clearInterval(waitForFields)
        loadFlightFieldsNearby(viewer, startLat, startLon)
        setStatus('fieldsLoaded')
      }
    }, 500)

    // Bei Kamera-Bewegung: neue Daten laden
    let moveTimeout: any = null
    viewer.camera.moveEnd.addEventListener(() => {
      if (moveTimeout) clearTimeout(moveTimeout)
      moveTimeout = setTimeout(() => {
        const pos = viewer.camera.positionCartographic
        const lat = Cesium.Math.toDegrees(pos.latitude)
        const lon = Cesium.Math.toDegrees(pos.longitude)
        if (showAirspaceRef.current) loadAirspace(viewer, lat, lon)
        if (showFieldsRef.current) loadFlightFieldsNearby(viewer, lat, lon)
      }, 1500)
    })
  }

  // Alle Flugplätze einmal laden (für Gesamtzahl), aber nur Nahbereich anzeigen
  useEffect(() => {
    fetch('/api/flying-fields/geojson')
      .then(r => r.json())
      .then(data => { allFieldsRef.current = data.features || []; setFieldsCount(data.features?.length || 0) })
      .catch(() => {})
  }, [])

  // Nur Flugplätze im Umkreis von 100km anzeigen
  // In Bodensicht (<500m Höhe): Plätze im 2km-Umkreis ausblenden (sonst stören sie)
  const loadFlightFieldsNearby = (viewer: any, centerLat: number, centerLon: number) => {
    // Alte Field-Entities entfernen
    const toRemove = viewer.entities.values.filter((e: any) => e.properties?.type?.getValue() === 'field')
    toRemove.forEach((e: any) => viewer.entities.remove(e))

    const cameraHeight = viewer.camera.positionCartographic?.height || 10000
    const isGroundLevel = cameraHeight < 500

    // Sichtbereich dynamisch nach Kamerahöhe:
    // Niedrig (< 2km) → nur ~20km Umkreis, max 50 Plätze
    // Mittel (2-20km) → ~80km Umkreis, max 150 Plätze
    // Hoch (20-100km) → ~150km Umkreis, max 300 Plätze
    // Sehr hoch (>100km) → ~200km Umkreis, max 500 Plätze
    let maxDist: number, maxCount: number
    if (cameraHeight < 2000) { maxDist = 0.2; maxCount = 50 }
    else if (cameraHeight < 20000) { maxDist = 0.8; maxCount = 150 }
    else if (cameraHeight < 100000) { maxDist = 1.5; maxCount = 300 }
    else { maxDist = 2.0; maxCount = 500 }

    const nearby = allFieldsRef.current.filter((f: any) => {
      const [lon, lat] = f.geometry.coordinates
      if (Math.abs(lat - centerLat) > maxDist || Math.abs(lon - centerLon) > maxDist) return false
      // In Bodensicht: Plätze im 2km-Umkreis ausblenden
      if (isGroundLevel) {
        const dist = Math.sqrt(Math.pow(lat - centerLat, 2) + Math.pow(lon - centerLon, 2))
        if (dist < 0.018) return false
      }
      return true
    }).slice(0, maxCount)

    for (const f of nearby) {
      const [lon, lat] = f.geometry.coordinates
      const p = f.properties
      const isSlope = p.terrain === 'slope'

      viewer.entities.add({
          name: p.name,
          position: Cesium.Cartesian3.fromDegrees(lon, lat),
          billboard: {
            image: isSlope
              ? 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48"><polygon points="24,3 45,42 3,42" fill="#ef4444" stroke="#fff" stroke-width="3"/><text x="24" y="36" text-anchor="middle" font-size="18" font-weight="bold" fill="#fff">S</text></svg>')
              : 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48"><circle cx="24" cy="24" r="20" fill="#3b82f6" stroke="#fff" stroke-width="3"/><text x="24" y="31" text-anchor="middle" font-size="20" font-weight="bold" fill="#fff">F</text></svg>'),
            width: 42,
            height: 42,
            verticalOrigin: Cesium.VerticalOrigin.CENTER,
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            scaleByDistance: new Cesium.NearFarScalar(500, 1.3, 80000, 0.35),
            translucencyByDistance: new Cesium.NearFarScalar(500, 1.0, 250000, 0.3),
          },
          label: {
            text: `${p.name}${p.country ? '  (' + p.country + ')' : ''}`,
            font: 'bold 14px sans-serif',
            fillColor: Cesium.Color.WHITE,
            outlineColor: Cesium.Color.BLACK,
            outlineWidth: 3,
            style: Cesium.LabelStyle.FILL_AND_OUTLINE,
            showBackground: true,
            backgroundColor: isSlope
              ? Cesium.Color.fromCssColorString('#ef4444').withAlpha(0.8)
              : Cesium.Color.fromCssColorString('#3b82f6').withAlpha(0.8),
            backgroundPadding: new Cesium.Cartesian2(8, 4),
            verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
            pixelOffset: new Cesium.Cartesian2(0, -24),
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            scaleByDistance: new Cesium.NearFarScalar(1000, 1.0, 40000, 0.35),
            translucencyByDistance: new Cesium.NearFarScalar(1000, 1.0, 60000, 0.0),
          },
          description: `<div style="font-family:system-ui;padding:10px;line-height:1.5">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px">
              <span style="display:inline-block;width:14px;height:14px;border-radius:${isSlope ? '0' : '50%'};background:${isSlope ? '#ef4444' : '#3b82f6'}"></span>
              <h3 style="margin:0;font-size:15px">${p.name}</h3>
            </div>
            <table style="font-size:12px;border-collapse:collapse;width:100%">
              <tr><td style="padding:2px 8px 2px 0;color:#888;white-space:nowrap">Typ:</td><td style="padding:2px 0"><strong>${isSlope ? '🔴 Hangfluggelände (Slope)' : '🔵 Modellflugplatz (Flatland)'}</strong></td></tr>
              <tr><td style="padding:2px 8px 2px 0;color:#888">Land:</td><td style="padding:2px 0">${p.country || '?'}</td></tr>
              ${p.description ? `<tr><td style="padding:2px 8px 2px 0;color:#888">Info:</td><td style="padding:2px 0">${p.description}</td></tr>` : ''}
              ${p.website_url ? `<tr><td style="padding:2px 8px 2px 0;color:#888">Web:</td><td style="padding:2px 0"><a href="#" onclick="try{window.parent.postMessage({type:'navigate',url:'${p.website_url.replace(/'/g, "\\'")}'},'*')}catch(e){};return false" style="color:#3b82f6;cursor:pointer">${p.website_url.replace(/https?:\/\//, '').slice(0, 35)}</a></td></tr>` : ''}
              <tr><td style="padding:2px 8px 2px 0;color:#888">Position:</td><td style="padding:2px 0;font-family:monospace;font-size:11px">${lat.toFixed(5)}, ${lon.toFixed(5)}</td></tr>
            </table>
            <p style="font-size:11px;color:#60a5fa;margin-top:10px;font-weight:bold">
              ✈ Doppelklick auf den Platz = direkt anfliegen
            </p>
            <p style="font-size:9px;color:#888;margin-top:6px;line-height:1.3">
              ⛳ Golf-Modus = Luftraum prüfen · Quelle: arnobertz.de · Angaben ohne Gewähr
            </p>
          </div>`,
          properties: { type: 'field', terrain: p.terrain },
          show: showFieldsRef.current,
        })
      }
      setStatus(`${nearby.length} Flugplätze im Sichtbereich`)
  }

  // Lufträume laden (OpenAIP Polygone)
  const loadAirspace = async (viewer: any, lat: number, lon: number) => {
    try {
      const res = await fetch(`/api/airspace?lat=${lat.toFixed(2)}&lon=${lon.toFixed(2)}&radius=150`)
      const data = await res.json()
      const features = data.features || []

      // Alte Airspace-Entities entfernen
      const toRemove = viewer.entities.values.filter((e: any) => e.properties?.type?.getValue() === 'airspace')
      toRemove.forEach((e: any) => viewer.entities.remove(e))

      for (const f of features) {
        if (!f.geometry?.coordinates?.[0]) continue

        const coords = f.geometry.coordinates[0]
        const positions = coords.map((c: number[]) => Cesium.Cartesian3.fromDegrees(c[0], c[1]))
        const p = f.properties

        // Farbe nach ICAO-Klasse + Typ — kontrastreich + unterscheidbar
        let color, outlineColor

        // Zuerst nach ICAO-Klasse
        switch (p.icaoClass) {
          case 0: // A — Rot, voll kontrolliert (Oberer Luftraum)
            color = Cesium.Color.fromCssColorString('#dc2626').withAlpha(0.15)
            outlineColor = Cesium.Color.fromCssColorString('#dc2626')
            break
          case 1: // B — Dunkelrot
            color = Cesium.Color.fromCssColorString('#b91c1c').withAlpha(0.15)
            outlineColor = Cesium.Color.fromCssColorString('#b91c1c')
            break
          case 2: // C — Magenta/Pink (wie ICAO-Karte)
            color = Cesium.Color.fromCssColorString('#c026d3').withAlpha(0.15)
            outlineColor = Cesium.Color.fromCssColorString('#c026d3')
            break
          case 3: // D — Blau (CTR typisch)
            color = Cesium.Color.fromCssColorString('#2563eb').withAlpha(0.18)
            outlineColor = Cesium.Color.fromCssColorString('#2563eb')
            break
          case 4: // E — Gelb/Gold (wie ICAO-Karte, häufigster kontrollierter)
            color = Cesium.Color.fromCssColorString('#ca8a04').withAlpha(0.12)
            outlineColor = Cesium.Color.fromCssColorString('#eab308')
            break
          case 5: // F — Grau-Blau
            color = Cesium.Color.fromCssColorString('#6b7280').withAlpha(0.1)
            outlineColor = Cesium.Color.fromCssColorString('#9ca3af')
            break
          case 6: // G — Grün (Modellflug erlaubt)
            color = Cesium.Color.fromCssColorString('#16a34a').withAlpha(0.06)
            outlineColor = Cesium.Color.fromCssColorString('#22c55e')
            break
          default: // SUA / Unbekannt
            color = Cesium.Color.fromCssColorString('#f59e0b').withAlpha(0.12)
            outlineColor = Cesium.Color.fromCssColorString('#f59e0b')
        }

        // Überschreiben nach Sonder-Typ (R/D/P haben Vorrang)
        if (p.type === 3) { // PROHIBITED — Dunkelrot, kräftig
          color = Cesium.Color.fromCssColorString('#7f1d1d').withAlpha(0.3)
          outlineColor = Cesium.Color.fromCssColorString('#ef4444')
        }
        if (p.type === 1) { // RESTRICTED — Rot schraffiert
          color = Cesium.Color.fromCssColorString('#dc2626').withAlpha(0.22)
          outlineColor = Cesium.Color.fromCssColorString('#f87171')
        }
        if (p.type === 2) { // DANGER — Orange
          color = Cesium.Color.fromCssColorString('#ea580c').withAlpha(0.2)
          outlineColor = Cesium.Color.fromCssColorString('#fb923c')
        }
        if (p.type === 5) { // TMZ — Cyan
          color = Cesium.Color.fromCssColorString('#0891b2').withAlpha(0.1)
          outlineColor = Cesium.Color.fromCssColorString('#06b6d4')
        }
        if (p.type === 4) { // CTR — Blau, kräftiger
          color = Cesium.Color.fromCssColorString('#1d4ed8').withAlpha(0.2)
          outlineColor = Cesium.Color.fromCssColorString('#3b82f6')
        }
        if (p.type === 7) { // TMA — Violett
          color = Cesium.Color.fromCssColorString('#7c3aed').withAlpha(0.15)
          outlineColor = Cesium.Color.fromCssColorString('#a78bfa')
        }

        // Höhen korrekt umrechnen
        // unit: 1=ft, 6=FL (Flight Level: FL100 = 10000ft)
        // referenceDatum: 0=GND (über Grund/AGL), 1=MSL, 2=STD
        const toMetersRaw = (limit: any) => {
          if (!limit) return 0
          let ft = limit.value || 0
          if (limit.unit === 6) ft = ft * 100 // FL → ft
          return ft * 0.3048
        }
        const isGND = (limit: any) => limit?.referenceDatum === 0
        const isLowerGND = isGND(p.lowerLimit)
        const isUpperGND = isGND(p.upperLimit)
        const lowerRawM = toMetersRaw(p.lowerLimit)
        const upperRawM = toMetersRaw(p.upperLimit)

        const fmtLimit = (limit: any) => {
          if (!limit) return 'GND'
          if (limit.unit === 6) return `FL${limit.value}`
          const ref = limit.referenceDatum === 0 ? 'AGL' : limit.referenceDatum === 1 ? 'MSL' : 'STD'
          return `${limit.value} ft ${ref}`
        }

        const icaoNames = ['A', 'B', 'C', 'D', 'E', 'F', 'G', '?', 'SUA']
        const icaoName = icaoNames[p.icaoClass] || '?'

        // Darstellung: Alle Höhen als absolute MSL (HeightReference.NONE)
        // Cesium unterstützt RELATIVE_TO_GROUND nicht für extruded Polygons
        // GND mit Wert 0 → CLAMP_TO_GROUND (direkt am Boden)
        const lowerIsZeroGND = isLowerGND && (p.lowerLimit?.value || 0) === 0

        // Für die visuelle Darstellung: GND-Werte als MSL approximieren
        // (Bei Golf-Klick wird exakt mit Bodenhöhe gerechnet)
        const lowerM = lowerRawM
        const upperM = Math.max(upperRawM, lowerRawM + 30)

        viewer.entities.add({
          name: `${p.name} (${icaoName})`,
          polygon: {
            hierarchy: new Cesium.PolygonHierarchy(positions),
            material: color,
            outline: true,
            outlineColor,
            outlineWidth: 2,
            extrudedHeight: upperM,
            height: lowerIsZeroGND ? 0 : lowerM,
            extrudedHeightReference: Cesium.HeightReference.NONE,
            heightReference: lowerIsZeroGND ? Cesium.HeightReference.CLAMP_TO_GROUND : Cesium.HeightReference.NONE,
            closeTop: true,
            closeBottom: true,
          },
          description: `<div style="font-family:system-ui;padding:8px">
            <h3>${p.name}</h3>
            <p><strong>Klasse ${icaoName}</strong> · ${p.country || '?'}</p>
            <p>Untergrenze: ${fmtLimit(p.lowerLimit)} (${Math.round(lowerRawM)}m${isLowerGND ? ' AGL' : ' MSL'})</p>
            <p>Obergrenze: ${fmtLimit(p.upperLimit)} (${Math.round(upperRawM)}m${isUpperGND ? ' AGL' : ' MSL'})</p>
            <p style="font-size:11px;color:#888;margin-top:8px">Quelle: OpenAIP · Keine Gewähr</p>
          </div>`,
          properties: {
            type: 'airspace',
            icaoClass: p.icaoClass,
            airspaceType: p.type,
            lowerGND: isLowerGND,
            upperGND: isUpperGND,
            lowerRawM: lowerRawM,
            upperRawM: upperRawM,
          },
          show: showAirspaceRef.current,
        })

        // Label in der Mitte des Luftraums
        const centerLat = coords.reduce((s: number, c: number[]) => s + c[1], 0) / coords.length
        const centerLon = coords.reduce((s: number, c: number[]) => s + c[0], 0) / coords.length
        const labelHeight = (lowerM + upperM) / 2

        const typeNames: Record<number, string> = {
          0: '', 1: 'R', 2: 'D', 3: 'P', 4: 'CTR', 5: 'TMZ', 6: 'RMZ', 7: 'TMA',
          8: 'TRA', 9: 'TSA', 13: 'ATZ', 14: 'MATZ', 26: 'CTA',
        }
        const typeName = typeNames[p.type] || ''

        viewer.entities.add({
          position: Cesium.Cartesian3.fromDegrees(centerLon, centerLat, labelHeight),
          label: {
            text: `${icaoName} ${typeName}\n${p.name}\n${fmtLimit(p.lowerLimit)} — ${fmtLimit(p.upperLimit)}`,
            font: 'bold 14px sans-serif',
            fillColor: Cesium.Color.WHITE,
            outlineColor: Cesium.Color.BLACK,
            outlineWidth: 4,
            style: Cesium.LabelStyle.FILL_AND_OUTLINE,
            horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
            verticalOrigin: Cesium.VerticalOrigin.CENTER,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            showBackground: true,
            backgroundColor: outlineColor.withAlpha(0.7),
            backgroundPadding: new Cesium.Cartesian2(8, 5),
            scaleByDistance: new Cesium.NearFarScalar(3000, 1.0, 100000, 0.35),
            translucencyByDistance: new Cesium.NearFarScalar(3000, 1.0, 150000, 0.0),
          },
          properties: { type: 'airspace', icaoClass: p.icaoClass, airspaceType: p.type },
          show: showAirspaceRef.current,
        })
      }
      setAirspaceCount(features.length)
      setAirspaceLoaded(features.length > 0)
    } catch (err: any) {
      console.error('Airspace load error:', err)
      setAirspaceLoaded(false)
    }
  }

  // NOTAMs laden
  const loadNotams = async (viewer: any, lat: number, lon: number) => {
    try {
      const res = await fetch(`/api/notams?lat=${lat.toFixed(2)}&lon=${lon.toFixed(2)}&radius=80`)
      const data = await res.json()
      const features = data.features || []

      // Alte NOTAM-Entities entfernen
      const toRemove = viewer.entities.values.filter((e: any) => e.properties?.type?.getValue() === 'notam')
      toRemove.forEach((e: any) => viewer.entities.remove(e))

      for (const f of features) {
        if (!f.geometry?.coordinates) continue
        const [lon, lat] = f.geometry.type === 'Point' ? f.geometry.coordinates : f.geometry.coordinates[0]?.[0] || [0, 0]
        const p = f.properties

        viewer.entities.add({
          name: `NOTAM: ${p.id || 'Aktiv'}`,
          position: Cesium.Cartesian3.fromDegrees(lon, lat),
          billboard: {
            image: 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#ef4444" stroke="#fff" stroke-width="2"/><text x="12" y="16" text-anchor="middle" font-size="14" fill="#fff" font-weight="bold">!</text></svg>'),
            verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            scale: 1.2,
          },
          description: `<div style="font-family:system-ui;padding:8px;max-width:400px">
            <h3 style="color:#ef4444">NOTAM ${p.id || ''}</h3>
            <p style="font-size:12px;white-space:pre-wrap;word-break:break-word">${(p.text || '').slice(0, 500)}</p>
            ${p.effectiveStart ? `<p style="font-size:11px"><strong>Von:</strong> ${p.effectiveStart}</p>` : ''}
            ${p.effectiveEnd ? `<p style="font-size:11px"><strong>Bis:</strong> ${p.effectiveEnd}</p>` : ''}
            <p style="font-size:10px;color:#888;margin-top:8px">Quelle: FAA/aviationweather.gov · Nur zur Information</p>
          </div>`,
          properties: { type: 'notam' },
          show: showNotamsRef.current,
        })
      }
      setNotamCount(features.length)
    } catch (err: any) {
      console.error('NOTAM load error:', err)
    }
  }

  // Layer-Sichtbarkeit
  useEffect(() => {
    showFieldsRef.current = showFields
    showAirspaceRef.current = showAirspace
    showNotamsRef.current = showNotams
    golfModeRef.current = golfMode
    if (!viewerRef.current) return
    viewerRef.current.entities.values.forEach((e: any) => {
      const t = e.properties?.type?.getValue()
      if (t === 'field') e.show = showFields
      if (t === 'airspace') e.show = showAirspace
      if (t === 'notam') e.show = showNotams
    })
  }, [showAirspace, showNotams, showFields, golfMode])

  // Gate-Screens (vor dem Hauptinhalt, nach allen Hooks)
  if (checkingAccess) {
    return (
      <div data-award="true" className="min-h-screen relative overflow-x-clip" style={{ background: 'var(--bg-primary)' }}>
        <FglHeader />
      <GliderBackdrop />
        <main className="flex items-center justify-center pt-[150px]">
          <div className="text-[14px]" style={{ color: 'var(--award-cirrus-3)' }}>Zugang wird geprüft...</div>
        </main>
      </div>
    )
  }

  if (accessDenied) {
    return (
      <div data-award="true" className="min-h-screen relative overflow-x-clip" style={{ background: 'var(--bg-primary)' }}>
        <FglHeader />
        <main className="max-w-[800px] mx-auto px-4 py-24 text-center">
          <h1 className="award-h-lg mb-6">3D-Luftraum</h1>
          <div
            className="rounded-[20px] p-8"
            style={{ background: 'var(--award-ink-2)', border: '2px solid var(--award-gold)' }}
          >
            <p className="text-2xl mb-4">🔒</p>
            <h2 className="award-h-md mb-4" style={{ color: 'var(--award-gold)' }}>Pro-Feature</h2>
            <p className="text-[14px] mb-6" style={{ color: 'var(--award-cirrus-2)' }}>
              Der 3D-Luftraum ist ab dem Pro-Plan verfügbar (ab 25 validen Flügen).
              Lade deine IGC-Dateien hoch und schalte dieses Feature automatisch frei.
            </p>
            <Link href="/mitmachen" className="award-btn award-btn-gold">
              Jetzt Flüge hochladen
            </Link>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div data-award="true" className="min-h-screen relative overflow-x-clip" style={{ background: 'var(--bg-primary)' }}>
      {!disclaimerAccepted && <MapDisclaimer onAccept={handleDisclaimerAccept} type="3d" />}
      <FglHeader />

      {disclaimerAccepted && <>
      {/* Cesium JS + CSS laden */}
      <Script
        src="/cesium/Cesium.js"
        strategy="afterInteractive"
        onLoad={async () => {
          (window as any).CESIUM_BASE_URL = '/cesium/'
          // Ion-Token aus Map-Config laden (public API)
          try {
            const res = await fetch('/api/map-config')
            const data = await res.json()
            if (data.cesiumIonToken) {
              (window as any).__CESIUM_ION_TOKEN = data.cesiumIonToken
            }
          } catch {}
          initCesium()
        }}
      />
      <link rel="stylesheet" href="/cesium/Widgets/widgets.css" />
      <style>{`
        @keyframes blink-red {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>

      {/* Banner-Hoehe 2026-05-08 reduziert: schmaler, ein-zeiliger Glas-Streifen */}
      <div className="fixed left-0 right-0 z-20 px-4 py-1 text-center text-[10px] font-bold" style={{ top: 48, background: 'rgba(234,179,8,0.92)', color: '#000', borderBottom: '1px solid var(--border)', lineHeight: '1.4', letterSpacing: '0.01em' }}>
        {t('mapDisclaimer.bannerText')}
      </div>
      <div className="fixed left-0 right-0 bottom-0 z-20 px-4 py-1.5 text-center text-[10px] leading-snug" style={{ background: 'rgba(0,0,0,0.75)', color: '#bbb', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        {t('mapDisclaimer.footerText')}
      </div>

      <div className="fixed inset-0" style={{ background: '#000', zIndex: 1, paddingTop: 82, paddingBottom: 26 }}>
        {/* Cesium Container — volle Maus-Kontrolle */}
        <div ref={containerRef} className="w-full h-full" style={{ cursor: 'grab' }} />

        {/* Overlay Controls — pointer-events nur auf Buttons */}
        <div className="absolute top-[70px] left-3 z-10 flex flex-col gap-2" style={{ pointerEvents: 'none' }}>

          {/* WARNUNG wenn Lufträume nicht geladen */}
          {loaded && !airspaceLoaded && (
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl" style={{
              background: 'rgba(220,38,38,0.95)',
              color: '#fff',
              backdropFilter: 'blur(8px)',
              border: '2px solid #fca5a5',
              animation: 'blink-red 1.5s infinite',
              pointerEvents: 'auto',
            }}>
              <span style={{ fontSize: 20 }}>⚠️</span>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900 }}>{t('airspace3d.airspacesNotLoaded')}</div>
                <div style={{ fontSize: 10, opacity: 0.9 }}>{t('airspace3d.golfNotPossible')}</div>
              </div>
              <button onClick={() => {
                if (!viewerRef.current) return
                const pos = viewerRef.current.camera.positionCartographic
                loadAirspace(viewerRef.current, Cesium.Math.toDegrees(pos.latitude), Cesium.Math.toDegrees(pos.longitude))
              }} style={{
                background: '#fff', color: '#dc2626', border: 'none', borderRadius: 8,
                padding: '6px 14px', fontWeight: 900, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap',
              }}>
                {t('airspace3d.load')}
              </button>
            </div>
          )}

          {/* Status */}
          <div className="px-3 py-1.5 rounded-lg text-[10px] font-bold flex items-center gap-2"
               style={{ background: 'rgba(0,0,0,0.8)', color: '#fff', backdropFilter: 'blur(8px)', pointerEvents: 'auto' }}>
            <span>{t(`airspace3d.status_${status}`)}</span>
            <span style={{ color: fieldsCount > 0 ? '#60a5fa' : '#888' }}>● {fieldsCount} {t('airspace3d.fields')}</span>
            <span style={{ color: airspaceLoaded ? '#22c55e' : '#ef4444' }}>● {airspaceCount} {t('airspace3d.airspaces')} {!airspaceLoaded ? '⚠' : '✓'}</span>
          </div>

          {/* Layer Toggles */}
          <div className="flex gap-1 flex-wrap" style={{ pointerEvents: 'auto' }}>
            <button onClick={() => setShowFields(!showFields)}
              className="px-2.5 py-1.5 rounded-lg text-[10px] font-bold transition-all"
              style={{ background: showFields ? 'rgba(59,130,246,0.85)' : 'rgba(0,0,0,0.6)', color: '#fff' }}>
              {t('airspace3d.flyingFields')}
            </button>
            <a href="/flying-fields"
              className="px-2.5 py-1.5 rounded-lg text-[10px] font-bold transition-all no-underline"
              style={{ background: 'rgba(0,0,0,0.6)', color: '#fff' }}>
              {t('airspace3d.map2d')}
            </a>
            <button onClick={() => {
              setShowAirspace(!showAirspace)
            }}
              className="px-2.5 py-1.5 rounded-lg text-[10px] font-bold transition-all"
              style={{ background: showAirspace ? 'rgba(234,179,8,0.85)' : 'rgba(0,0,0,0.6)', color: '#fff' }}>
              {t('airspace3d.airspaces3d')}
            </button>
            {/* NOTAMs deaktiviert — nur FAA/USA verfügbar, für Europa nicht nutzbar */}
            <button onClick={() => {
              const newGolf = !golfMode
              setGolfMode(newGolf)
              // Golf aktiviert → Lufträume sichtbar machen
              if (newGolf && !showAirspace) setShowAirspace(true)
            }}
              className="px-2.5 py-1.5 rounded-lg text-[10px] font-bold transition-all"
              style={{
                background: golfMode ? 'rgba(34,197,94,0.9)' : 'rgba(0,0,0,0.6)',
                color: '#fff',
                border: golfMode ? '2px solid #22c55e' : '2px solid transparent',
                animation: golfMode ? 'pulse 2s infinite' : 'none',
              }}>
              {golfMode ? t('airspace3d.golfOn') : t('airspace3d.golfCheck')}
            </button>
            {/* Luftraum-Status-Indikator */}
            {loaded && airspaceLoaded && (
              <span className="px-2.5 py-1.5 rounded-lg text-[10px] font-bold"
                style={{ background: 'rgba(34,197,94,0.85)', color: '#fff' }}>
                {airspaceCount} {t('airspace3d.airspaces')}
              </span>
            )}
          </div>
        </div>

        {/* Legende — kompakt mit Accordions */}
        <div className="absolute bottom-3 left-3 z-10 rounded-lg text-[9px] max-w-[340px]"
             style={{ background: 'rgba(0,0,0,0.88)', color: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(8px)', pointerEvents: 'auto' }}>

          {/* Legende (immer sichtbar, kompakt) */}
          <div className="px-3 py-2">
            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 mb-1">
              <span><span style={{ color: '#22c55e' }}>■</span> G {t('airspace3d.legendUncontrolled')}</span>
              <span><span style={{ color: '#eab308' }}>■</span> E {t('airspace3d.legendPartCtrl')}</span>
              <span><span style={{ color: '#2563eb' }}>■</span> D CTR</span>
              <span><span style={{ color: '#c026d3' }}>■</span> C {t('airspace3d.legendControlled')}</span>
              <span><span style={{ color: '#ef4444' }}>▲</span> Slope</span>
              <span><span style={{ color: '#3b82f6' }}>●</span> Flatland</span>
            </div>
            <p className="opacity-50">{t('airspace3d.legendHint')}</p>
          </div>

          {/* Quellen — Accordion */}
          <details style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <summary className="px-3 py-1.5 cursor-pointer select-none font-bold text-[9px]" style={{ color: '#aaa' }}>
              {t('airspace3d.dataSources')} ▾
            </summary>
            <div className="px-3 pb-2 opacity-70 space-y-0.5">
              <p>● {t('airspace3d.srcAirspace')}: <strong>OpenAIP</strong></p>
              <p>● {t('airspace3d.srcFields')}: <strong>Arno Bertz</strong> — 11.000+</p>
              <p>● Terrain: <strong>Cesium World Terrain</strong></p>
              <p>● {t('airspace3d.srcMap')}: <strong>OpenStreetMap</strong></p>
            </div>
          </details>

          {/* Haftungsausschluss — Accordion */}
          <details style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <summary className="px-3 py-1.5 cursor-pointer select-none font-bold text-[9px]" style={{ color: '#ef4444' }}>
              {t('airspace3d.disclaimer')} ▾
            </summary>
            <div className="px-3 pb-2" style={{ color: '#fca5a5', fontSize: '9px', lineHeight: '1.4' }}>
              <p style={{ marginBottom: '3px' }}>
                Diese 3D-Darstellung des Luftraums einschließlich der Golf-Höhenberechnung dient
                ausschließlich der <strong>unverbindlichen Vorinformation</strong>.
                Luftraumdaten (OpenAIP) können <strong>unvollständig, veraltet oder fehlerhaft</strong> sein.
                NOTAMs, kurzfristige Änderungen und lokale Auflagen werden <strong>nicht vollständig</strong> dargestellt.
              </p>
              <p style={{ marginBottom: '3px', color: '#fff' }}>
                <strong>Der Pilot ist verpflichtet</strong>, vor jedem Flug bei den zuständigen
                Flugsicherungen einzuholen
                (<a href="https://www.dfs.de" target="_top" style={{ color: '#60a5fa' }}>DFS</a>,{' '}
                <a href="https://www.austrocontrol.at" target="_top" style={{ color: '#60a5fa' }}>Austrocontrol</a>,{' '}
                <a href="https://www.skyguide.ch" target="_top" style={{ color: '#60a5fa' }}>Skyguide</a>,{' '}
                <a href="https://www.enac.gov.it" target="_top" style={{ color: '#60a5fa' }}>ENAC</a>,{' '}
                <a href="https://www.ecologie.gouv.fr/direction-generale-laviation-civile-dgac" target="_top" style={{ color: '#60a5fa' }}>DGAC</a>):
              </p>
              <p style={{ paddingLeft: '6px', marginBottom: '3px' }}>
                • Luftraumstruktur am Aufstiegsort<br/>
                • Aktive NOTAMs + temporäre Beschränkungen<br/>
                • Platzregelungen + Genehmigungen
              </p>
              <p style={{ marginBottom: '3px' }}>
                Durch Rechenfehler in der Golf-Höhenberechnung oder fehlerhafte
                Darstellung der Luftraumgrenzen können <strong>Missverständnisse
                </strong> entstehen. Dieser Bereich wurde letztmalig am 02.04.2026
                ausschließlich im Bereich Deutschland getestet.
              </p>
              <p style={{ color: '#fff' }}>
                <strong>Jegliche Haftung für Schäden, Bußgelder oder Rechtsnachteile
                aus der Nutzung dieser 3D-Karte, der Golf-Berechnung oder der
                Flugplatzdaten ist ausgeschlossen.</strong>
              </p>
            </div>
          </details>
        </div>
      </div>
      </>}
    </div>
  )
}
